import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  /** Settles when the previous /cart/change has, so the server applies changes in the order they were made. */
  #updateQueue = Promise.resolve();
  #latestUpdateId = 0;
  /**
   * What the optimistic empty-bag swap replaced, so an update that never reached the store can put it back.
   * @type {{ nodes: ChildNode[], heading: Element | null, parent: Node | null, next: Node | null, swapped: Promise<unknown> } | null}
   */
  #beforeEmpty = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh; running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself, so no fallback is needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh: the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Read the row from the stepper itself: a morph during the debounce can renumber the lines.
    const lineItemRow = /** @type {HTMLElement | null} */ (event.target.closest('[data-key]'));

    this.updateQuantity({
      line,
      key: lineItemRow?.dataset.key,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    const update = this.updateQuantity({
      line,
      key: cartItemRowToRemove?.dataset.key,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    // Removed rows stay in the DOM, collapsed, until the server answers: the next morph drops them, a failure
    // brings them back. Only rows still showing count.
    const shownRows = this.refs.cartItemRows.filter(
      (row) => !row.classList.contains('removing') && !row.classList.contains('hidden')
    );
    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...shownRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Keyboard and screen-reader users are otherwise dropped on <body> when the focused row goes.
    const hadFocus = rowsToRemove.some((row) => row.contains(document.activeElement));

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == shownRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      const heading = document.getElementById('cart-drawer-heading');
      // What the swap below replaces.
      const nodes = [...this.childNodes];
      const parent = heading?.parentNode ?? null;
      const next = heading?.nextSibling ?? null;
      // After the click has finished dispatching: a button already swapped out is outside the drawer panel, so
      // the drawer reads the click as a backdrop click and closes.
      const swapped = new Promise((resolve) => setTimeout(resolve)).then(() =>
        startViewTransition(() => {
          heading?.remove();
          this.replaceChildren(clone);
        }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page'])
      );
      this.#beforeEmpty = { nodes, heading, parent, next, swapped };

      // After the update: the drawer's hydration morph replaces the empty state's nodes, focus included. A failed
      // update puts the row back instead, remove button and all.
      if (hadFocus) {
        update.then(() => {
          const target =
            this.querySelector('.cart-items__empty-button') ?? cartItemRowToRemove.querySelector('.cart-items__remove');
          if (target instanceof HTMLElement) target.focus();
        });
      }

      return;
    }

    if (hadFocus) {
      const remainingRows = shownRows.filter((row) => !rowsToRemove.includes(row));
      const isAfterRemoved = (/** @type {HTMLElement} */ row) =>
        Boolean(cartItemRowToRemove.compareDocumentPosition(row) & Node.DOCUMENT_POSITION_FOLLOWING);
      const nextRow = remainingRows.find(isAfterRemoved) ?? remainingRows.at(-1);
      /** @type {HTMLElement | null | undefined} */ (nextRow?.querySelector('.cart-items__title'))?.focus();
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      if (prefersReducedMotion()) return row.classList.add('hidden');

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.key] - The line's key. Sent instead of the line number, which points at another item
   *   once the bag changes in another window.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    // Queued, not aborted: aborting cancels the response but not the mutation, so an older response could still
    // land last. Queued, the server applies changes in order and only the newest response is morphed.
    const updateId = ++this.#latestUpdateId;

    this.#disableCartItems();

    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(key ? { id: key } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: key ?? '', quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    const request = this.#updateQueue.then(() =>
      fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
    );
    this.#updateQueue = request.catch(() => {});

    try {
      const response = await request;
      // A 422 carries its reason in `errors`; a proxy or rate limiter may not answer with JSON at all.
      const data = await response.json().catch(() => ({}));
      const isLatest = updateId === this.#latestUpdateId;

      if (isLatest) resetShimmer(this);

      if (data.errors || !response.ok) {
        const message = data.errors || data.description || data.message || response.statusText || 'Cart update failed';
        deferredUpdatePromise.reject(new Error(message));
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        if (isLatest) await this.#handleCartError(line, message, key, quantity);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(data.sections[this.sectionId], 'text/html');

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections,
          items: data.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      // An older response lacks the changes queued behind it; morphing it would flash stale quantities.
      if (!isLatest) return;

      this.#beforeEmpty = null;

      // Rows removed optimistically go first, with the whitespace before them: left in, either one shifts the
      // morph's position match, so it moves the rows after them up, and moving the focused row blurs it.
      for (const row of this.querySelectorAll('.cart-items__table-row.removing, .cart-items__table-row.hidden')) {
        await Promise.allSettled(row.getAnimations().map((animation) => animation.finished));
        if (row.previousSibling?.nodeType === Node.TEXT_NODE) row.previousSibling.remove();
        row.remove();
      }

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(data);

      await morphSection(this.sectionId, data.sections[this.sectionId], {
        mode: this.isDrawer ? 'hydration' : 'full',
      });

      this.#updateCartQuantitySelectorButtonStates();

      // Changing a line removed in another window is a no-op on the server: say why the item disappeared.
      const items = /** @type {{ key: string }[]} */ (data.items ?? []);
      if (key && quantity > 0 && !items.some((item) => item.key === key)) {
        this.#showError(null, this.#alertMessage('lineGone'));
      }
    } catch (error) {
      console.error(error);
      deferredUpdatePromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error && error.message ? error.message : 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      // The request never reached the store (offline, or the connection dropped).
      if (updateId === this.#latestUpdateId) {
        resetShimmer(this);
        await this.#handleCartError(line, this.#alertMessage('offline'));
      }
    } finally {
      // A newer queued update owns the disabled state.
      if (updateId === this.#latestUpdateId) this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Puts the bag back to what the server holds and shows the server's reason on the line, or on the bag.
   * @param {number} line - The line.
   * @param {string} message - The server's error message.
   * @param {string} [key] - The line's key.
   * @param {number} [quantity] - The quantity that was refused.
   */
  async #handleCartError(line, message, key, quantity) {
    const hadFocus = this.contains(document.activeElement);

    // The refused change is already on screen: the input holds the refused quantity, or the row (or the whole
    // bag) was optimistically removed. Re-render so the bag matches the server again.
    let lineGone = false;
    try {
      await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' });
      lineGone = Boolean(key) && !this.querySelector(`[data-key="${CSS.escape(key ?? '')}"]`);
    } catch {
      // The store is out of reach, so undo the optimistic changes by hand.
      if (this.#beforeEmpty) {
        const { nodes, heading, parent, next, swapped } = this.#beforeEmpty;
        // A fetch that fails at once can land before the view transition has swapped the empty state in.
        await swapped;
        this.replaceChildren(...nodes);
        if (heading && parent) parent.insertBefore(heading, next);
      }
      for (const row of this.querySelectorAll('.cart-items__table-row')) row.classList.remove('removing', 'hidden');
      for (const input of this.querySelectorAll('input[data-cart-line]')) {
        if (input instanceof HTMLInputElement) input.value = input.defaultValue;
      }
      // The restored total was mid-shimmer when the swap took it out.
      resetShimmer(this);
    }
    this.#beforeEmpty = null;
    this.#updateCartQuantitySelectorButtonStates();

    // The focused control can go with the re-render (its line was removed elsewhere).
    if (hadFocus && !this.contains(document.activeElement)) {
      const target = this.querySelector('.cart-items__title, .cart-items__empty-button');
      if (target instanceof HTMLElement) target.focus();
    }

    if (!lineGone) return this.#showError(line, message);

    // Removed in another window: the server answers "no valid id or line parameter", which means nothing to a buyer,
    // and the header still counts the removed item.
    this.#syncCartCount();
    // A removal of it already got the buyer what they wanted.
    if (quantity) this.#showError(null, this.#alertMessage('lineGone'));
  }

  /**
   * Tells the header (and any other bag on the page) what the server holds after a change made elsewhere.
   */
  async #syncCartCount() {
    const cart = await this.fetchCartData().catch(() => null);
    if (!cart) return;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { itemCount: cart.item_count, source: 'cart-items-component', didError: false },
    });
  }

  /**
   * Shows a message on the line, or on the bag when there is no line to show it on.
   * @param {number | null} line - The line.
   * @param {string} message - The message.
   */
  #showError(line, message) {
    let errorContainer = line ? this.querySelector(`[ref="cartItemErrorContainer-${line}"]`) : null;
    let errorText = line ? this.querySelector(`[ref="cartItemError-${line}"]`) : null;
    if (!errorContainer || !errorText) {
      errorContainer = this.querySelector('[ref="cartAlert"]');
      errorText = this.querySelector('[ref="cartAlertText"]');
    }
    if (!errorContainer || !errorText) return;

    // Unhide, then write on the next frame: role="alert" announces text that changes while it is rendered.
    errorContainer.classList.remove('hidden');
    requestAnimationFrame(() => {
      errorText.textContent = message;
    });
  }

  /**
   * Plain-sentence copy for failures the server has no words for, carried on the bag-level alert.
   * @param {'lineGone' | 'offline'} name
   * @returns {string}
   */
  #alertMessage(name) {
    const alert = /** @type {HTMLElement | null} */ (this.querySelector('[ref="cartAlert"]'));
    return alert?.dataset[name] ?? '';
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty to non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
